import { useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, ListChecks, Check } from "lucide-react";

/**
 * Full-screen, phone-friendly step-by-step cooking mode.
 * Keeps the screen awake via the Screen Wake Lock API while open.
 */
export function CookMode({ recipe, open, onClose }) {
  const steps = recipe?.steps || [];
  const ingredients = recipe?.ingredients || [];
  const [i, setI] = useState(0);
  const [showIng, setShowIng] = useState(false);
  const [checked, setChecked] = useState({});
  const [awake, setAwake] = useState(false);
  const lockRef = useRef(null);

  // Wake Lock lifecycle
  useEffect(() => {
    if (!open) return;
    let released = false;
    const acquire = async () => {
      try {
        if ("wakeLock" in navigator) {
          lockRef.current = await navigator.wakeLock.request("screen");
          setAwake(true);
          lockRef.current.addEventListener?.("release", () => setAwake(false));
        }
      } catch { setAwake(false); }
    };
    const onVis = () => { if (document.visibilityState === "visible" && !released) acquire(); };
    acquire();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      released = true;
      document.removeEventListener("visibilitychange", onVis);
      try { lockRef.current?.release?.(); } catch { /* ignore */ }
      lockRef.current = null;
      setAwake(false);
    };
  }, [open]);

  useEffect(() => { if (open) { setI(0); setShowIng(false); } }, [open]);

  if (!open) return null;
  const total = steps.length;
  const atEnd = i >= total - 1;

  return (
    <div className="fixed inset-0 z-[80] bg-[#0E0D0B] text-bone flex flex-col" data-testid="cook-mode">
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-blaze">Cook mode</p>
          <h2 className="font-serif text-xl sm:text-2xl truncate">{recipe?.name}</h2>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full ${awake ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-white/50"}`} data-testid="cook-mode-awake">
            {awake ? "Screen stays on" : "Screen"}
          </span>
          <button onClick={onClose} aria-label="Exit cook mode" className="text-bone/70 hover:text-bone" data-testid="cook-mode-close"><X size={26} /></button>
        </div>
      </div>

      {/* Progress */}
      <div className="h-1 bg-white/10">
        <div className="h-full bg-blaze transition-all duration-300" style={{ width: `${total ? ((i + 1) / total) * 100 : 0}%` }} />
      </div>

      {/* Step body */}
      <div className="flex-1 overflow-auto px-6 sm:px-10 flex flex-col justify-center">
        <div className="max-w-2xl mx-auto w-full py-10">
          {total > 0 ? (
            <>
              <p className="text-blaze text-sm font-bold uppercase tracking-[0.2em]">Step {i + 1} of {total}</p>
              <p className="font-serif text-3xl sm:text-5xl leading-[1.15] mt-5" data-testid="cook-mode-step">{steps[i]}</p>
            </>
          ) : (
            <p className="font-serif text-3xl text-center text-bone/70">No steps available for this recipe yet.</p>
          )}
        </div>
      </div>

      {/* Ingredients drawer */}
      {showIng && (
        <div className="border-t border-white/10 bg-[#141210] max-h-[45vh] overflow-auto px-6 sm:px-10 py-5" data-testid="cook-mode-ingredients">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-blaze mb-3">Ingredients</p>
          <ul className="space-y-1 max-w-2xl mx-auto">
            {ingredients.map((ing, idx) => (
              <li key={idx}>
                <button onClick={() => setChecked((c) => ({ ...c, [idx]: !c[idx] }))} className="w-full flex items-center gap-3 py-2 text-left" data-testid={`cook-ing-${idx}`}>
                  <span className={`w-5 h-5 rounded border grid place-items-center shrink-0 ${checked[idx] ? "bg-blaze border-blaze" : "border-white/30"}`}>{checked[idx] && <Check size={13} />}</span>
                  <span className={`text-base ${checked[idx] ? "line-through text-white/40" : "text-bone/90"}`}>{ing}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Bottom controls */}
      <div className="border-t border-white/10 px-5 py-4 flex items-center gap-3">
        <button onClick={() => setShowIng((s) => !s)} className={`inline-flex items-center gap-2 px-4 py-3 rounded-full text-sm font-semibold border transition-colors ${showIng ? "bg-white/15 border-white/20" : "border-white/20 text-bone/80 hover:bg-white/10"}`} data-testid="cook-mode-ingredients-toggle">
          <ListChecks size={17} /> <span className="hidden sm:inline">Ingredients</span>
        </button>
        <div className="flex-1 flex items-center gap-3 justify-end">
          <button onClick={() => setI((n) => Math.max(0, n - 1))} disabled={i === 0} className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full border border-white/20 text-sm font-semibold disabled:opacity-30 hover:bg-white/10 transition-colors" data-testid="cook-mode-prev"><ChevronLeft size={18} /> Back</button>
          {atEnd ? (
            <button onClick={onClose} className="inline-flex items-center gap-1.5 px-7 py-3 rounded-full bg-blaze text-white text-sm font-semibold hover:bg-blaze-light transition-colors" data-testid="cook-mode-done">Done <Check size={17} /></button>
          ) : (
            <button onClick={() => setI((n) => Math.min(total - 1, n + 1))} className="inline-flex items-center gap-1.5 px-7 py-3 rounded-full bg-blaze text-white text-sm font-semibold hover:bg-blaze-light transition-colors" data-testid="cook-mode-next">Next <ChevronRight size={18} /></button>
          )}
        </div>
      </div>
    </div>
  );
}
